/**
 * Conversation Transfer — column model.
 *
 * Reference ID is the pinned identity column: with the from/to pairs, the
 * classification and the org placement all scrolling horizontally, a row whose
 * conversation has scrolled away is unreadable.
 */
import type { ReportColumnDef } from '../agent-performance/columns.js'

export const CONVERSATION_TRANSFER_REPORT_ID = 'conversation.transfers'

export const CONVERSATION_TRANSFER_COLUMNS: ReportColumnDef[] = [
  { key: 'referenceId', label: 'Reference ID', width: 180, stickyLeft: 0, locked: true, type: 'mono' },
  { key: 'transferredAt', label: 'Transferred At', width: 200, type: 'datetime' },
  { key: 'bucket', label: 'Interval', width: 200, type: 'datetime' },
  { key: 'subject', label: 'Subject', width: 300, type: 'text' },
  { key: 'fromAgent', label: 'Transferred From', width: 200, type: 'text' },
  { key: 'fromTeam', label: 'From Team', width: 180, type: 'text' },
  { key: 'fromKind', label: 'From Type', width: 140, align: 'center', type: 'badge' },
  { key: 'toAgent', label: 'Transferred To', width: 200, type: 'text' },
  { key: 'toTeam', label: 'To Team', width: 180, type: 'text' },
  { key: 'toQueue', label: 'To Queue', width: 180, type: 'text' },
  { key: 'toKind', label: 'To Type', width: 140, align: 'center', type: 'badge' },
  { key: 'transferType', label: 'Transfer Type', width: 170, type: 'badge' },
  { key: 'reason', label: 'Reason', width: 240, type: 'text' },
  { key: 'description', label: 'Description', width: 280, type: 'text' },
  { key: 'isPrivate', label: 'Private', width: 130, align: 'center', type: 'boolean' },
  { key: 'channelOrigin', label: 'Channel', width: 150, type: 'text' },
  { key: 'locationName', label: 'Location', width: 180, type: 'text' },
  { key: 'branchName', label: 'Branch', width: 180, type: 'text' },
  { key: 'departmentName', label: 'Department', width: 180, type: 'text' },
  { key: 'campaignName', label: 'Campaign', width: 200, type: 'text' },
  { key: 'openedAt', label: 'Conversation Created', width: 200, type: 'datetime' },
  { key: 'closedAt', label: 'Conversation Closed', width: 200, type: 'datetime' },
]

export const TRANSFER_DEFAULT_COLUMNS: string[] = CONVERSATION_TRANSFER_COLUMNS.map((c) => c.key)

export function sanitizeTransferColumns(saved: unknown): string[] {
  if (!Array.isArray(saved)) return TRANSFER_DEFAULT_COLUMNS
  const known = new Set(TRANSFER_DEFAULT_COLUMNS)
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const col of CONVERSATION_TRANSFER_COLUMNS) {
    if (col.locked && !seen.has(col.key)) {
      seen.add(col.key)
      ordered.unshift(col.key)
    }
  }
  return ordered.length > 0 ? ordered : TRANSFER_DEFAULT_COLUMNS
}

/** Read a cell for the search filter — rows are untyped API objects. */
export function readTransferCell(row: Record<string, unknown>, key: string): unknown {
  return row[key]
}
